import {ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {BehaviorSubject, finalize, Observable, of} from 'rxjs';
import {filter, map, switchMap, take} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {locationSetTypesFilterFieldMap as fieldMap} from '@emm/shared/formly/maps/location-set-types-filter.config';
import {LocationSetTypesFormComponent} from '../location-set-types-form/location-set-types-form.component';
import {MatDialog} from '@angular/material/dialog';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {ContextMenuAction} from '@emm/core/models/context-menu-action.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {DialogService} from '@emm/shared/services/dialog.service';
import {LocationSetTypesFacadeService} from '@emm/management/services/location-set-types-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {
    AddLocationSetTypeDialogComponent
} from '@emm/shared/dialogs/add-location-set-type-dialog/add-location-set-type-dialog.component';
import {SearchFilter} from '@emm/core/models/search-filter';
import {UserService} from '@emm/core/services/user.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {submitFormWithDetail} from '@emm/core/util/rx.util';

@Component({
    selector: 'emm-location-set-types',
    styleUrls: ['./location-set-types.component.scss'],
    templateUrl: './location-set-types.component.html',
    standalone: false
})
export class LocationSetTypesComponent implements OnInit {

    @ViewChild(LocationSetTypesFormComponent) locationSetTypesForm: LocationSetTypesFormComponent;

    anyLoading$ = this.locationSetTypesFacade.anyLoading$;
    centerLoading$ = this.locationSetTypesFacade.centerLoading$;
    fields$ = of(null).pipe(map((_) => [fieldMap['name'](), fieldMap['weight']()]));
    isReadOnly$ = this.authFacade.isReadOnly$;
    isReadOnly = this.userService.hasLoginRole();
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isAuthor = this.userService.hasAuthorRole();
    isMMSAuthor = this.userService.isMmsAuthorOnly();
    itemDetail$ = this.locationSetTypesFacade.locationSetTypeDetail$;
    locationSetTypes$ = new BehaviorSubject<any>(undefined);
    locationSetTypesAsGenericItems$ = this.locationSetTypesFacade.locationSetTypesAsGenericItems$;
    prevLocationSetType: LocationSetType;
    rightLoading$ = this.locationSetTypesFacade.rightLoading$;
    locationSetTypesCache = [];

    locationSetTypesContextMenuActions = [
        {
            // Delete
            enabled: (locationSetType) => !locationSetType.locked,
            click: (locationSetType) => this.onDeleteLocationSetType(locationSetType),
            visible: this.isSuperAdmin ? R.T : R.F,
            icon: R.always('delete'),
            text: R.always('Delete')
        }
    ] as ContextMenuAction<LocationSetType>[];
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    constructor(
        private authFacade: AuthFacadeService,
        private dialogService: DialogService,
        private locationSetTypesFacade: LocationSetTypesFacadeService,
        private notificationService: NotificationService,
        private api: ManagementApiService,
        private changeDetector: ChangeDetectorRef,
        private dialog: MatDialog,
        private userService: UserService,
    ) {}


    ngOnInit(): void {
        this.locationSetTypesFacade.init();
        this.anyLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.getLocationSetTypes();
    }


    getLocationSetTypes() {
        this.locationSetTypesFacade.startProgressBar();
        this.locationSetTypesAsGenericItems$
            .pipe(
                finalize(() => this.locationSetTypesFacade.stopProgressBar()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
            this.locationSetTypesCache = result;
            this.locationSetTypes$.next(this.locationSetTypesCache);
        });
    }


    openCreateLocationSetTypeDialog(): void {
        const dialogRef = this.dialog.open(AddLocationSetTypeDialogComponent, {
            width: '600px',
            data: {showDelete: false},
        });

        dialogRef.afterClosed()
            .pipe(
                filter((result) => result && result === 'submit'),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
            this.notificationService.success('Added Successfully');
            this.getLocationSetTypes();
        });
    }


    onFilter(searchFilter: SearchFilter): void {
        this.filterNodes(searchFilter.value as string, searchFilter.key);
    }


    filterNodes(value: string, selected: string): void {
        this.locationSetTypes$.next(this.locationSetTypesCache);
        this.locationSetTypes$.pipe(
            take(1),
            map(list => {
                let filteredNodes;
                try {
                    switch (selected) {
                        case selected:
                            filteredNodes = list.filter(nd =>
                                nd['source'][selected].toLowerCase().includes(value.toLowerCase()));
                            break;
                        default:
                            filteredNodes = list.filter(nd => nd['name'].toLowerCase().includes(value.toLowerCase()));
                            break;
                    }
                    this.locationSetTypes$.next(filteredNodes);
                } catch (e) {}
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }


    onSelectLocationSetType(selected: LocationSetType): void {
        this.locationSetTypesFacade.startProgressBar();
        if (RA.isNotNil(selected)) {
            this.checkIfItemHasChanges(this.prevLocationSetType)
                .pipe(
                    take(1),
                    finalize(() => this.locationSetTypesFacade.stopProgressBar()),
                    takeUntilDestroyed(this.destroyRef))
                .subscribe((_) => {
                    this.prevLocationSetType = selected;
                    this.locationSetTypesFacade.selectLocationSetType(selected);
                });
        } else {
            this.prevLocationSetType = null;
            this.locationSetTypesFacade.stopProgressBar();
        }
    }


    onDeleteLocationSetType(locationSetType: LocationSetType): void {
        const dialogRef = this.dialogService.openConfirmDialog('Are you sure you want to delete',
            locationSetType.name, null);
        try {
            dialogRef.afterClosed()
                .pipe(
                    switchMap(result => result ? this.locationSetTypesFacade.api.deleteLocationSetType(locationSetType.tag) : of(false)),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: (response) => {
                        if (response === false) {
                            this.notificationService.error(`Delete was cancelled`);
                        } else {
                            this.notificationService.success(`Successfully deleted ${locationSetType.name}`);
                            this.getLocationSetTypes();
                        }
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    }
                });
        } catch (e) {
            this.notificationService.error(`Failed to delete ${locationSetType.name}`);
        }
    }


    checkIfItemHasChanges(prevLocationSetType: LocationSetType): Observable<boolean> {
        return this.isReadOnly$.pipe(
            take(1),
            switchMap((isReadOnly) => {
                if (this.hasChanges() && !isReadOnly && RA.isNotNil(prevLocationSetType)) {
                    return this.dialogService
                        .openSaveChangesDialog(prevLocationSetType.name)
                        .afterClosed()
                        .pipe(
                            switchMap(res => {
                                if (res) {
                                    this.locationSetTypesForm.submit(prevLocationSetType.tag, false);
                                    return this.locationSetTypesFacade.updateLocationSetType$ as Observable<boolean>;
                                }
                                this.locationSetTypesForm.cancel();
                                return of(true);
                            })
                        );
                }

                return of(true);
            })
        );
    }


    hasChanges(): boolean {
        return this.locationSetTypesForm && this.locationSetTypesForm.hasChanges();
    }

    submitForm(): void {
        submitFormWithDetail(this.itemDetail$, this.locationSetTypesForm)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }

}
